/**
 * remove an item from an array at the specified index and return a new array
 * @param array
 * @param index
 * @returns {*[]}
 */
export const removeArrayItemAtIndex = (array, index) => [
  ...array.slice(0, index),
  ...array.slice(index + 1)
];

/**
 * check if all the contents of an array equal another array, regardless of order
 * optionally supply a comparison function
 * @param a {Array}
 * @param b {Array}
 * @param compareFn {Function}
 * @returns {boolean}
 */
export const arraysEqual = (a, b, compareFn = (a, b) => a === b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  const arrA = a.concat().sort();
  const arrB = b.concat().sort();

  if (compareFn) {
    return arrA.every((val, index) => compareFn(val, arrB[index]));
  }

  return arrA.every((val, index) => val === arrB[index]);
};

/**
 * replace an item from an array at the specified index and return a new array
 * @param array
 * @param index
 * @returns {*[]}
 */
export const replaceArrayItemAtIndex = (array, item, index) => [
  ...array.slice(0, index),
  item,
  ...array.slice(index + 1)
];

/**
 * add an item to an array at the specified index and return a new array
 * @param array
 * @param item
 * @param index
 * @returns {*[]}
 */
export const addArrayItemAtIndex = (array, item, index) => [
  ...array.slice(0, index),
  item,
  ...array.slice(index)
];

/**
 * map and filter an array at the same time, thus reducing iterations
 * @param array
 * @param filter
 * @param map
 */
export const arrayFilterAndMap = (array, filter, map) => array.reduce((result, current) => {
  if (filter(current)) {
    result.push(map(current));
  }

  return result;
}, []);

/**
 * get all the unique values into a new array
 * @param array
 * @param uniqueByFn
 * @returns {[]}
 */
export const unique = (array, uniqueByFn = null) => {
  const newArray = [];
  const map = {};

  for (let i = 0; i < array.length; i++) {
    let stringValue = uniqueByFn ? uniqueByFn(array[i]) : array[i];

    try {
      stringValue = JSON.stringify(stringValue);
    } catch (e) {
      // do nothing
    }

    if (!map[stringValue]) {
      newArray.push(array[i]);
      map[stringValue] = true;
    }
  }

  return newArray;
};

/**
 * update an array item with a matching value using a modification function
 * @param array
 * @param keyName
 * @param value
 * @param modificationFunction
 */
export const modifyArrayItemAt = (array, keyName, value, modificationFunction) => array.map(element => {
  const isFunctionMatch = (typeof keyName === 'function' && keyName(element));
  const isValueMatch = (typeof keyName === 'string' && element[keyName] === value);
  const modifier = typeof value === 'function' ? value : modificationFunction;

  if (typeof modifier !== 'function') {
    throw new Error('you must pass a function as the 3rd or 4th argument');
  }

  if (isFunctionMatch || isValueMatch) {
    if (Array.isArray(element)) {
      return [...modifier(element)];
    }

    if (typeof element === 'object') {
      return { ...modifier(element) };
    }

    return modifier(element);
  }

  return element;
});

/**
 * remove an array item with a matching value
 * @param array
 * @param keyName
 * @param value
 */
export const removeArrayItemAt = (array, keyName, value) => array.filter(element => {
  if (value && typeof element === 'object' && element[keyName] === value) {
    return false;
  }

  if (value === undefined) {
    return element !== keyName;
  }

  return true;
});

/**
 * replace an array item with a matching value
 * @param array
 * @param keyName
 * @param value
 * @param newItem
 */
export const replaceArrayItemAt = (array, keyName, value, newItem) => array.map(element => {
  if (element[keyName] === value) {
    return newItem;
  }

  return element;
});

/**
 * get an value from an object at a specified path
 * @param input
 * @param path
 * @param defaultValue
 * @param shouldThrow
 * @param allowFalsyValues - return default value if value is falsy
 * @returns {null|*}
 */
export const get = (
  input,
  path,
  defaultValue = null,
  shouldThrow = false,
  allowFalsyValues = true
) => {
  if (typeof path === 'function') {
    return path(input);
  }

  if (typeof path === 'string') {
    path = path.split('.');
  }

  if (path.length === 0) {
    return input;
  }

  const key = path.shift();

  if (input[key] !== undefined) {
    if (typeof input[key] === 'object') {
      return get(input[key], path);
    }

    if (!input[key] && !allowFalsyValues) {
      return defaultValue;
    }

    return input[key];
  }

  if (shouldThrow) {
    throw new Error(`Item at path ${path} not found in object`);
  }

  return defaultValue;
};

/**
 * set an object value at a specific path
 * @param obj
 * @param path
 * @param val
 */
export const set = (obj, path, val) => {
  function stringToPath(stringPath) {
    if (typeof stringPath !== 'string') return stringPath;

    const output = [];

    stringPath.split('.').forEach(item => {
      item.split(/\[([^}]+)\]/g).forEach(key => {
        if (key.length > 0) {
          output.push(key);
        }
      });
    });

    return output;
  }

  path = stringToPath(path);

  const { length } = path;
  let current = obj;

  path.forEach((key, index) => {
    const isArray = key.slice(-2) === '[]';

    key = isArray ? key.slice(0, -2) : key;

    if (isArray && !Array.isArray(current[key])) {
      current[key] = [];
    }

    if (index === length - 1) {
      if (isArray) {
        current[key].push(val);
      } else {
        current[key] = val;
      }
    } else {
      if (!current[key]) {
        current[key] = {};
      }

      current = current[key];
    }
  });
};

/**
 * create a hash map from an array using the value found at a path
 * @param inputArray
 * @param path
 * @param valuesAsArrays
 * @returns {*}
 */
export function createMap(inputArray, path = null, valuesAsArrays = false) {
  return inputArray.reduce((hashmap, element) => {
    const keyValue = path
      ? get(element, path)
      : element;

    if (!keyValue) return hashmap;

    if (valuesAsArrays) {
      return { ...hashmap, [keyValue]: [...(hashmap[keyValue] || []), element] };
    }

    return { ...hashmap, [keyValue]: element };
  }, {});
}

/**
 * subtracts the items in one array from another array, returning a new array
 * @param arr1
 * @param arr2
 * @returns {*}
 */
export function subtractArrays(arr1, arr2) {
  return arr1
    .filter(item => !arr2.includes(item));
}

/**
 * curried sort callback to sort alphanumerically by a specific key
 * @param a
 * @param b
 * @returns {number}
 */
export function sortAlphaNumeric(key = null) {
  return (a, b) => {
    const isNumber = v => (+v).toString() === v;

    const aValue = key ? get(a, key) : a;
    const bValue = key ? get(b, key) : b;

    const aPart = aValue.match(/\d+|\D+/g) || '';
    const bPart = bValue.match(/\d+|\D+/g) || '';

    let i = 0; const len = Math.min(aPart.length, bPart.length);
    while (i < len && aPart[i] === bPart[i]) { i++; }

    if (i === len) {
      return aPart.length - bPart.length;
    }

    if (isNumber(aPart[i]) && isNumber(bPart[i])) {
      return aPart[i] - bPart[i];
    }

    return aPart[i].localeCompare(bPart[i]);
  };
}

/**
 * Slice an array into chunks of a specific size
 * @param array {Array}
 * @param size {Integer}
 * @returns {Array}
 */
export function chunkArray(array, size) {
  const res = [];
  for (let i = 0; i < array.length; i += size) {
    const chunk = array.slice(i, i + size);
    res.push(chunk);
  }

  return res;
}

export const stringFromArray = (array, sep = ',', property) => (
  array[0] ? array.slice(1).reduce((acc, item) => acc + sep + item[property], array[0][property]) : ''
);

export const createUniqueNumberArray = array => {
  const uniqueNumbers = new Set();
  const result = [];

  for (let i = 0; i < array.length; i++) {
    const number = array[i];

    if (!uniqueNumbers.has(number)) {
      uniqueNumbers.add(number);
      result.push(number);
    }
  }

  return result;
};
